'use client';

import { KeyboardArrowDown } from '@mui/icons-material';
import { Box, Button, List, ListItem, Typography, useTheme } from '@mui/material';
import { IconDots } from '@tabler/icons-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { MouseEvent, ReactNode, useEffect, useRef, useState } from 'react';

import { trackEvent } from '@/lib/analytics/gtm';
import { NestedMenu, NestedMenuItem } from '@/lib/queries/Menus';

import { CollapseWithSlide } from './MenuComponents';
import {
  DESKTOP_MAX_TOP_LEVEL_ITEMS,
  DESKTOP_MAX_TOP_LEVEL_ITEMS_XL,
  SUBMENU_TIMEOUT,
  TOP_LEVEL_MENU_TIMEOUT,
} from './MenuConstants';
import {
  closeAllMenus as closeAllMenusHelper,
  getDisplayText,
  getIndentationPadding,
  getMenuItemUrl,
  hasSubmenu,
  isMenuItemActive,
  isMenuItemActiveParent,
} from './MenuHelpers';
import { createMenuStyles, getArrowRotation } from './MenuStyles';
import { DropdownProps, MenuItemProps } from './MenuTypes';

interface DesktopMegaMenuProps {
  menu: NestedMenu;
  variant: 'dark' | 'light';
  maxLevelAdjust?: number; // Optional prop to adjust how many top-level items show before "More" based on screen size
  maxLevelAdjustXL?: number; // Optional prop to adjust how many top-level items show before "More" based on screen size
}

// Private component interfaces
interface DesktopMenuItemProps extends MenuItemProps {
  isLastItem?: boolean;
}

const DesktopMenuItem = ({
  item,
  index,
  currentPath,
  expandedItems,
  onToggleExpanded,
  onNavigate,
  getMenuItemUrl,
  getDisplayText,
  renderSubmenuItems,
  isLastItem = false,
}: DesktopMenuItemProps) => {
  const theme = useTheme();
  const styles = createMenuStyles(theme);
  const key = `desktop-${item.__typename}-${index}`;
  const itemHasSubmenu = hasSubmenu(item);
  const url = getMenuItemUrl(item);
  const text = getDisplayText(item);

  const isActive = isMenuItemActive(item, currentPath);
  const isActiveParent = isMenuItemActiveParent(item, currentPath);
  const isExpanded = expandedItems[key] || false;

  const buttonSx = {
    ...styles.menuItem.base,
    ...styles.menuItem.button,
    ...styles.menuItem.desktopTopLevel,
    ...(isActive && styles.menuItem.active),
    ...(isActiveParent && styles.menuItem.active),
  };
  if (itemHasSubmenu) {
    return (
      <Box
        key={key}
        data-component="DesktopMenuItem__submenu"
        className={`mega-menu-item mega-menu-item--submenu ${
          isExpanded ? 'mega-menu-item--expanded' : ''
        } ${isActiveParent ? 'mega-menu-item--active-parent' : ''}
         ${isLastItem ? 'mega-menu-item--last' : ''}`}
        sx={{ position: 'relative' }}
      >
        <Button
          onClick={() => onToggleExpanded(key)}
          endIcon={<KeyboardArrowDown sx={getArrowRotation(isExpanded)} />}
          sx={buttonSx}
          className="mega-menu-item__button menu-item-level-0"
        >
          {text}
        </Button>
        <CollapseWithSlide in={isExpanded} timeout={TOP_LEVEL_MENU_TIMEOUT}>
          <Box
            sx={{
              ...styles.dropdown.paperFirstLevel,
              position: 'absolute',
              top: '100%',
              ...(isLastItem ? { right: 0 } : { left: 0 }),
              zIndex: 1000,
              maxHeight: 'calc(100vh - 6rem)',
              overflowY: 'auto',
            }}
          >
            <List sx={{ py: 0, pt: 1 }}>
              {renderSubmenuItems(item.SubMenu?.MenuItems || [], 1, key)}
            </List>
          </Box>
        </CollapseWithSlide>
      </Box>
    );
  }

  return (
    <Button
      key={key}
      component={Link}
      href={url}
      onClick={() => {
        trackEvent({
          feature: 'navigation',
          action: 'nav_header_click',
          params: { link_text: text, link_href: url },
        });
        onNavigate();
      }}
      sx={buttonSx}
      data-component="DesktopMenuItem__link"
      className={`mega-menu-item mega-menu-item--link menu-item-level-0 ${isActive ? 'mega-menu-item--active' : ''}`}
    >
      {text}
    </Button>
  );
};

const MoreDropdown = ({
  items,
  currentPath,
  expandedItems,
  onToggleExpanded,
  onNavigate,
  getMenuItemUrl,
  getDisplayText,
  renderSubmenuItems,
}: DropdownProps) => {
  const theme = useTheme();
  const styles = createMenuStyles(theme);
  const moreKey = 'more-dropdown';
  const isExpanded = expandedItems[moreKey] || false;

  return (
    <Box
      data-component="MoreDropdown"
      className={`mega-menu-more mega-menu-more-items-${items.length} ${isExpanded ? 'mega-menu-more--expanded' : ''}`}
      sx={{
        position: 'relative',
      }}
    >
      <Button
        onClick={() => onToggleExpanded(moreKey)}
        endIcon={<KeyboardArrowDown sx={getArrowRotation(isExpanded)} />}
        sx={{
          ...styles.menuItem.base,
          ...styles.menuItem.button,
          ...styles.menuItem.desktopTopLevel,
          minWidth: 'auto',
          pr: 0,
          // Show expanded state styling when menu is open
          ...(isExpanded && {
            ...styles.menuItem.base[':hover,:focus,:focus-within'],
          }),
        }}
        className="mega-menu-more__button mega-menu-item mega-menu-item--more menu-item-level-0"
        aria-label="View more menu items"
      >
        <IconDots />
      </Button>
      <CollapseWithSlide in={isExpanded} timeout={TOP_LEVEL_MENU_TIMEOUT}>
        <Box
          sx={{
            ...styles.dropdown.paperFirstLevel,
            position: 'absolute',
            top: '100%',
            right: 0,
            zIndex: 1000,
            maxHeight: 'calc(100vh - 6rem)',
            overflowY: 'auto',
          }}
        >
          <List sx={{ py: 0, pt: 1 }}>
            {items.map((item, index) => {
              const itemHasSubmenu = hasSubmenu(item);
              const shouldRenderAsSubmenu = itemHasSubmenu;
              const url = getMenuItemUrl(item);
              const text = getDisplayText(item);
              const key = `more-${item.__typename}-${index}`;

              const isActive = isMenuItemActive(item, currentPath);
              const isActiveParent = isMenuItemActiveParent(item, currentPath);

              if (shouldRenderAsSubmenu) {
                const submenuExpanded = expandedItems[key] || false;
                return (
                  <Box key={key}>
                    <ListItem sx={styles.menuItem.listItem}>
                      <Button
                        fullWidth
                        onClick={() => {
                          onToggleExpanded(key);
                        }}
                        endIcon={<KeyboardArrowDown sx={getArrowRotation(submenuExpanded)} />}
                        className={`mega-menu-item mega-menu-item--submenu menu-item-level-1 ${isActiveParent ? 'mega-menu-item--active-parent' : ''}`}
                        sx={{
                          ...styles.menuItem.base,
                          ...styles.menuItem.button,
                          ...styles.menuItem.desktopSubmenu,
                          justifyContent: 'space-between',
                          textAlign: 'left',
                        }}
                      >
                        <Typography variant="small">{text}</Typography>
                      </Button>
                    </ListItem>
                    <CollapseWithSlide in={submenuExpanded} timeout={SUBMENU_TIMEOUT}>
                      <List component="div" disablePadding>
                        {renderSubmenuItems(item.SubMenu?.MenuItems || [], 2, key)}
                      </List>
                    </CollapseWithSlide>
                  </Box>
                );
              } else {
                return (
                  <Box key={key}>
                    <ListItem sx={styles.menuItem.listItem}>
                      <Button
                        fullWidth
                        component={Link}
                        href={url}
                        onClick={() => {
                          trackEvent({
                            feature: 'navigation',
                            action: 'nav_header_click',
                            params: { link_text: text, link_href: url },
                          });
                          onNavigate();
                        }}
                        className={`mega-menu-item mega-menu-item--link menu-item-level-1 ${isActive ? 'mega-menu-item--active' : ''}`}
                        sx={{
                          ...styles.menuItem.base,
                          ...styles.menuItem.button,
                          ...styles.menuItem.desktopSubmenu,
                          ...(isActive && styles.menuItem.activeSubmenu),
                        }}
                      >
                        <Typography variant="small">{text}</Typography>
                      </Button>
                    </ListItem>
                  </Box>
                );
              }
            })}
          </List>
        </Box>
      </CollapseWithSlide>
    </Box>
  );
};

export const DesktopMegaMenu = ({
  menu,
  variant,
  maxLevelAdjust = 0,
  maxLevelAdjustXL = 0,
}: DesktopMegaMenuProps) => {
  const desktopMaxTopLeveItems = DESKTOP_MAX_TOP_LEVEL_ITEMS + maxLevelAdjust;
  const desktopMaxTopLeveItemsXL = DESKTOP_MAX_TOP_LEVEL_ITEMS_XL + maxLevelAdjustXL;

  const theme = useTheme();
  const menuRef = useRef<HTMLDivElement>(null);
  const currentPath = usePathname();

  const [expandedItems, setExpandedItems] = useState<{
    [key: string]: boolean;
  }>({});

  // Find the main menu
  const menuItems = menu?.MenuItems || [];

  // Filter out menu items that have submenus but no menu items (data inconsistency)
  const validMenuItems = menuItems.filter((item) => {
    if (item.__typename === 'ComponentMenuMenuSubMenu') {
      // Exclude items with null SubMenu and null SubMenuCustomLabel
      if (item.SubMenu === null && item.SubMenuCustomLabel === null) {
        return false;
      }
      // If it's a submenu with a SubMenu, make sure it has menu items
      if (item.SubMenu) {
        return item.SubMenu.MenuItems && item.SubMenu.MenuItems.length > 0;
      }
    }
    return true; // Keep all other menu item types
  });

  const topLevelItems = validMenuItems.slice(0, desktopMaxTopLeveItems);
  const xlTopLevelItems = validMenuItems.slice(0, desktopMaxTopLeveItemsXL);
  const moreMenuItems = validMenuItems.slice(desktopMaxTopLeveItems);
  const xlMoreMenuItems = validMenuItems.slice(desktopMaxTopLeveItemsXL);

  // Close all dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        // Close all expanded items
        setExpandedItems({});
      }
    };

    // Convert to the correct event type for document.addEventListener
    const handleDocumentClick = (event: Event) => {
      handleClickOutside(event as unknown as MouseEvent);
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, []);

  const handleToggleExpanded = (itemKey: string) => {
    setExpandedItems((prev) => {
      const isCurrentlyExpanded = prev[itemKey];

      if (isCurrentlyExpanded) {
        // If item is currently expanded, close it and all its children
        const newState = { ...prev };
        newState[itemKey] = false;

        // Close all child submenus (items that start with this key)
        Object.keys(newState).forEach((key) => {
          if (key.startsWith(`${itemKey}-`)) {
            newState[key] = false;
          }
        });

        // Special case: if closing the more dropdown, also close all 'more-' submenus
        if (itemKey === 'more-dropdown') {
          Object.keys(newState).forEach((key) => {
            if (key.startsWith('more-')) {
              newState[key] = false;
            }
          });
        }

        return newState;
      } else {
        // If item is not expanded, open it
        const newState = { ...prev };

        // Check if this is a top-level menu item or the more dropdown
        const dashCount = (itemKey.match(/-/g) || []).length;
        const isTopLevelItem =
          (itemKey.startsWith('desktop-') && dashCount === 2) || itemKey === 'more-dropdown';

        if (isTopLevelItem) {
          // For top-level menus and more dropdown, close all other top-level menus
          const keysToClose: string[] = [];

          Object.keys(newState).forEach((key) => {
            const keyDashCount = (key.match(/-/g) || []).length;
            if ((key.startsWith('desktop-') && keyDashCount === 2) || key === 'more-dropdown') {
              keysToClose.push(key);
              // Also collect all child submenus of this top-level menu
              Object.keys(newState).forEach((childKey) => {
                if (childKey.startsWith(`${key}-`)) {
                  keysToClose.push(childKey);
                }
              });
            }
          });

          // Close all collected keys
          keysToClose.forEach((key) => {
            newState[key] = false;
          });

          // Special case: if we're closing the more dropdown, also close all 'more-' submenus
          if (keysToClose.includes('more-dropdown')) {
            Object.keys(newState).forEach((key) => {
              if (key.startsWith('more-')) {
                newState[key] = false;
              }
            });
          }
        } else {
          // For submenus, only close siblings at the same level
          const keyParts = itemKey.split('-');
          let parentKey = '';

          // Find the parent key by removing the last typename-depth-index pattern
          for (let i = 0; i < keyParts.length - 3; i++) {
            if (i > 0) parentKey += '-';
            parentKey += keyParts[i];
          }

          // Close all sibling submenus (items with the same parent but different last part)
          Object.keys(newState).forEach((key) => {
            if (
              key !== itemKey &&
              key.startsWith(parentKey) &&
              key.split('-').length === keyParts.length
            ) {
              // This is a sibling at the same level, close it and all its children
              newState[key] = false;
              Object.keys(newState).forEach((childKey) => {
                if (childKey.startsWith(`${key}-`)) {
                  newState[childKey] = false;
                }
              });
            }
          });
        }

        newState[itemKey] = true;
        return newState;
      }
    });
  };

  // Function to close all menus when navigating
  const closeAllMenus = () => {
    closeAllMenusHelper(setExpandedItems);
  };

  // Helper function for rendering submenu items (desktop only) with inline nesting
  const renderSubmenuItems = (
    items: NestedMenuItem[],
    depth: number = 0,
    parentKey: string = ''
  ): ReactNode[] => {
    const result: ReactNode[] = [];
    const styles = createMenuStyles(theme);

    items.forEach((subItem, index) => {
      const text = getDisplayText(subItem);
      const key = `${parentKey}-${subItem.__typename}-${depth}-${index}`;
      const itemHasSubmenu = hasSubmenu(subItem);
      const url = getMenuItemUrl(subItem);
      const isActive = isMenuItemActive(subItem, currentPath);
      const isActiveParent = isMenuItemActiveParent(subItem, currentPath);

      // Check if this item has both MenuUrl and MenuItems (combined menu item)
      const hasBothUrlAndItems =
        subItem.MenuUrl && subItem.MenuItems && subItem.MenuItems.length > 0;

      if (itemHasSubmenu) {
        const subMenuItems = subItem.MenuItems || subItem.SubMenu?.MenuItems || [];
        if (!subMenuItems?.length || subMenuItems.length === 0) {
          return null;
        }

        // All submenus use inline toggle behavior
        const isExpanded = expandedItems[key] || false;

        result.push(
          <Box key={key} sx={{ position: 'relative' }}>
            <Button
              fullWidth
              onClick={() => {
                handleToggleExpanded(key);
              }}
              endIcon={<KeyboardArrowDown sx={getArrowRotation(isExpanded)} />}
              className={`mega-menu-item mega-menu-item--submenu menu-item-level-${depth + 1} ${isActiveParent ? 'mega-menu-item--active-parent' : ''}`}
              sx={{
                ...styles.menuItem.base,
                ...styles.menuItem.button,
                ...styles.menuItem.desktopSubmenu,
                pl: getIndentationPadding(depth),
                justifyContent: 'space-between',
                textAlign: 'left',
              }}
            >
              <Typography variant="small">{text}</Typography>
            </Button>
            <CollapseWithSlide in={isExpanded} timeout={SUBMENU_TIMEOUT}>
              <List component="div" disablePadding>
                {/* If this item has both URL and MenuItems, add the page link first */}
                {hasBothUrlAndItems && (
                  <ListItem sx={styles.menuItem.listItem}>
                    <Button
                      fullWidth
                      component={Link}
                      href={url}
                      onClick={() => {
                        trackEvent({
                          feature: 'navigation',
                          action: 'nav_header_click',
                          params: { link_text: text, link_href: url },
                        });
                        closeAllMenus();
                      }}
                      className={`mega-menu-item mega-menu-item--link menu-item-level-${depth + 2} ${isActive ? 'mega-menu-item--active' : ''}`}
                      sx={{
                        ...styles.menuItem.base,
                        ...styles.menuItem.button,
                        ...styles.menuItem.desktopSubmenu,
                        pl: getIndentationPadding(depth + 1),
                        ...(isActive && styles.menuItem.activeSubmenu),
                        fontWeight: 'bold', // Make the parent page link stand out
                      }}
                    >
                      <Typography variant="small">{text}</Typography>
                    </Button>
                  </ListItem>
                )}
                {renderSubmenuItems(subMenuItems, depth + 1, key)}
              </List>
            </CollapseWithSlide>
          </Box>
        );
      } else {
        // Regular menu item
        result.push(
          <ListItem key={key} sx={styles.menuItem.listItem}>
            <Button
              fullWidth
              component={Link}
              href={url}
              onClick={() => {
                trackEvent({
                  feature: 'navigation',
                  action: 'nav_header_click',
                  params: { link_text: text, link_href: url },
                });
                closeAllMenus();
              }}
              className={`mega-menu-item mega-menu-item--link menu-item-level-${depth + 1} ${isActive ? 'mega-menu-item--active' : ''}`}
              sx={{
                ...styles.menuItem.base,
                ...styles.menuItem.button,
                ...styles.menuItem.desktopSubmenu,
                pl: getIndentationPadding(depth),
                ...(isActive && styles.menuItem.activeSubmenu),
              }}
            >
              <Typography variant="small">{text}</Typography>
            </Button>
          </ListItem>
        );
      }
    });

    return result;
  };

  return (
    <Box
      ref={menuRef}
      sx={{
        alignItems: 'center',
        display: { xs: 'none', lg: 'flex' },
        gap: 2,
      }}
      data-component="DesktopMegaMenu"
      className={`menu-${variant}`}
    >
      {/* Render for lg breakpoint (3 items + more) */}
      <Box
        sx={{
          display: { lg: 'flex', xxl: 'none' },
          gap: 2,
          alignItems: 'center',
        }}
      >
        {topLevelItems.map((item, index) => {
          // Last top-level item should open from right (whether or not there's a More menu)
          const isLastVisibleItem = index === topLevelItems.length - 1;
          return (
            <Box
              key={`desktop-lg-${item.__typename}-${index}`}
              sx={{
                '.menu-item-level-0': {
                  '.menu-dark &': {
                    color: theme.palette.common.white,
                    borderBottom: `0.25rem solid ${theme.palette.purple[800]}`,
                  },
                },
              }}
            >
              <DesktopMenuItem
                item={item}
                index={index}
                currentPath={currentPath}
                expandedItems={expandedItems}
                onToggleExpanded={handleToggleExpanded}
                onNavigate={closeAllMenus}
                getMenuItemUrl={getMenuItemUrl}
                getDisplayText={getDisplayText}
                renderSubmenuItems={renderSubmenuItems}
                isLastItem={isLastVisibleItem}
              />
            </Box>
          );
        })}
        {moreMenuItems.length > 0 && (
          <Box
            sx={{
              '.menu-item-level-0': {
                '.menu-dark &': {
                  color: theme.palette.common.white,
                  borderBottom: `0.25rem solid ${theme.palette.purple[800]}`,
                },
              },
            }}
          >
            <MoreDropdown
              items={moreMenuItems}
              currentPath={currentPath}
              expandedItems={expandedItems}
              onToggleExpanded={handleToggleExpanded}
              onNavigate={closeAllMenus}
              getMenuItemUrl={getMenuItemUrl}
              getDisplayText={getDisplayText}
              renderSubmenuItems={renderSubmenuItems}
            />
          </Box>
        )}
      </Box>

      {/* Render for xxl breakpoint (5 items + more) */}
      <Box
        sx={{
          display: { lg: 'none', xxl: 'flex' },
          gap: 2,
          alignItems: 'center',
        }}
      >
        {xlTopLevelItems.map((item, index) => {
          // Last top-level item should open from right (whether or not there's a More menu)
          const isLastVisibleItem = index === xlTopLevelItems.length - 1;
          return (
            <Box
              key={`desktop-xxl-${item.__typename}-${index}`}
              sx={{
                '.menu-item-level-0': {
                  '.menu-dark &': {
                    color: theme.palette.common.white,
                    borderBottom: `0.25rem solid ${theme.palette.purple[800]}`,
                  },
                },
              }}
            >
              <DesktopMenuItem
                item={item}
                index={index}
                currentPath={currentPath}
                expandedItems={expandedItems}
                onToggleExpanded={handleToggleExpanded}
                onNavigate={closeAllMenus}
                getMenuItemUrl={getMenuItemUrl}
                getDisplayText={getDisplayText}
                renderSubmenuItems={renderSubmenuItems}
                isLastItem={isLastVisibleItem}
              />
            </Box>
          );
        })}
        {xlMoreMenuItems.length > 0 && (
          <Box
            sx={{
              '.menu-item-level-0': {
                '.menu-dark &': {
                  color: theme.palette.common.white,
                  borderBottom: `0.25rem solid ${theme.palette.purple[800]}`,
                },
              },
            }}
          >
            <MoreDropdown
              items={xlMoreMenuItems}
              currentPath={currentPath}
              expandedItems={expandedItems}
              onToggleExpanded={handleToggleExpanded}
              onNavigate={closeAllMenus}
              getMenuItemUrl={getMenuItemUrl}
              getDisplayText={getDisplayText}
              renderSubmenuItems={renderSubmenuItems}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
